<template>
  <div class="page">

    <!-- PAGE HEADER -->
    <div class="page-head">
      <div>
        <h1>Dashboard</h1>
      </div>

      <div style="display:flex; gap:10px;">
        <button
          v-if="canFeature('system')"
          class="btn ghost"
          @click="downloadDashboard"
        >
          <i class="bx bx-export"></i>
          Export Report
        </button>

        <button class="btn primary" @click="refresh">
          <i class="bx bx-refresh"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- METRICS -->
    <div class="grid cols-4">

      <!-- TOTAL USERS -->
      <div
        v-if="canFeature('users')"
        class="card"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
      >
        <div class="card-title">
          <strong>Total Users</strong>
          <span class="badge primary">LIVE</span>
        </div>

        <div class="metric">{{ loading ? "" : statsData.total_users }}</div>
        <div v-if="loading" class="skeleton" style="width:140px;margin-top:10px"></div>
        <div v-else class="mini">
          +{{ statsData.new_users_7d }} in last 7 days
        </div>
      </div>

      <!-- PENDING KYC -->
      <div
        v-if="canFeature('kyc')"
        class="card"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
      >
        <div class="card-title">
          <strong>Pending KYC</strong>
          <span class="badge warning">ACTION</span>
        </div>

        <div class="metric">{{ loading ? "" : statsData.pending_kyc }}</div>
        <div v-if="loading" class="skeleton" style="width:120px;margin-top:10px"></div>
        <div v-else class="mini">
          {{ statsData.urgent_kyc }} urgent requests
        </div>
      </div>

      <!-- WITHDRAWALS -->
      <div
        v-if="canFeature('withdrawals')"
        class="card"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
      >
        <div class="card-title">
          <strong>Withdrawals</strong>
          <span class="badge primary">TODAY</span>
        </div>

        <div class="metric">
          {{ loading ? "" : "₹" + fmtMoney(statsData.today_withdrawals_amount) }}
        </div>

        <div v-if="loading" class="skeleton" style="width:140px;margin-top:10px"></div>
        <div v-else class="mini">
          {{ statsData.today_withdrawals_count }} requests today
        </div>
      </div>

      <!-- SYSTEM HEALTH -->
      <div
        v-if="canFeature('system')"
        class="card"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0 }"
      >
        <div class="card-title">
          <strong>System Health</strong>
          <span class="badge success">OK</span>
        </div>

        <div class="metric">
          {{ loading ? "" : statsData.system_health + "%" }}
        </div>

        <div v-if="loading" class="skeleton" style="width:130px;margin-top:10px"></div>
        <div v-else class="mini">API latency stable</div>
      </div>

    </div>

    <!-- RECENT REQUESTS -->
    <div
      v-if="canFeature('recent')"
      class="card"
      style="margin-top:14px"
    >

      <div class="card-title">
        <strong>Recent Requests</strong>
        <button class="btn ghost">
          <i class="bx bx-right-arrow-alt"></i>
          View All
        </button>
      </div>

      <table class="table">
        <thead>
          <tr>
            <th>REQUEST</th>
            <th>USER</th>
            <th>DATE</th>
            <th>AMOUNT</th>
            <th>STATUS</th>
          </tr>
        </thead>

        <tbody v-if="!loading">
          <tr v-for="(r,idx) in requests" :key="idx">
            <td>
              {{ r.type }}
              <div class="row-muted">{{ r.ref }}</div>
            </td>
            <td>{{ r.user }}</td>
            <td class="row-muted">{{ r.date }}</td>
            <td>{{ r.amount }}</td>
            <td>
              <span class="status-pill" :class="r.statusClass">
                <i :class="r.icon"></i>
                {{ r.status }}
              </span>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr>
            <td colspan="5" style="padding:18px">
              <div class="skeleton" style="height:16px;width:90%;margin-bottom:12px"></div>
              <div class="skeleton" style="height:16px;width:80%;margin-bottom:12px"></div>
              <div class="skeleton" style="height:16px;width:85%"></div>
            </td>
          </tr>
        </tbody>
      </table>

    </div>

    <!-- QUICK ACTIONS -->
    <div
      v-if="canFeature('quick')"
      class="grid"
      style="margin-top:14px;grid-template-columns:1fr 1fr"
    >

      <!-- ACTIONS -->
      <div class="card">
        <div class="card-title">
          <strong>Quick Actions</strong>
          <span class="badge primary">TOOLS</span>
        </div>

        <div style="display:flex;gap:12px;flex-wrap:wrap">

          <button
            v-if="canFeature('kyc')"
            class="btn ghost"
          >
            <i class="bx bx-id-card"></i>
            Review KYC
          </button>

          <button
            v-if="canFeature('withdrawals')"
            class="btn ghost"
          >
            <i class="bx bx-money-withdraw"></i>
            Approve Withdrawals
          </button>

          <button class="btn ghost">
            <i class="bx bx-message-rounded"></i>
            Send Alert
          </button>

        </div>
      </div>

      <!-- REPAYMENTS SNAPSHOT -->
      <div
        v-if="canFeature('repayments')"
        class="card"
      >

        <div class="card-title">
          <strong>Repayments Snapshot</strong>
          <span class="badge primary">EMI</span>
        </div>

        <div class="mini">Quick loan collections overview</div>

        <div style="margin-top:12px;display:grid;gap:10px">

          <div class="snap-row">
            <span>Due Today</span>
            <span class="status-pill warning">
              {{ loading ? "-" : statsData.repayments_due_today }}
            </span>
          </div>

          <div class="snap-row">
            <span>Overdue EMIs</span>
            <span class="status-pill danger">
              {{ loading ? "-" : statsData.overdue_emi }}
            </span>
          </div>

          <div class="snap-row">
            <span>Collected (30d)</span>
            <span class="status-pill success">
              {{ loading ? "-" : "₹" + fmtMoney(statsData.collected_30d) }}
            </span>
          </div>

        </div>

      </div>

    </div>

  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { getDashboardStats, getRecentRequests,exportDashboardReportXl } from "@/apis/dashcon";

export default {
  name: "DashboardScreen",

  data() {
    return {
      toast: null,
      loading: true,
      userRole: "",

      ldata: null,
      admin_id: "",

      statsData: {
        total_users: "0",
        new_users_7d: "0",
        pending_kyc: "0",
        urgent_kyc: "0",
        today_withdrawals_amount: "0",
        today_withdrawals_count: "0",
        repayments_due_today: "0",
        overdue_emi: "0",
        collected_30d: "0",
        system_health: "99.8",
      },

      requests: [],

      employees: [
        { letter: "S", name: "Sahil Jain", role: "KYC Manager", status: "Online" },
        { letter: "N", name: "Neha Rai", role: "Operations", status: "Busy" },
        { letter: "A", name: "Aman Gupta", role: "Finance", status: "Offline" },
      ],
    };
  },

  methods: {
    canFeature(key) {
      const map = {
        SUPER_ADMIN: [
          "users","kyc","withdrawals","system","recent","quick","repayments"
        ],
        ADMIN_MANAGER: [
          "users","kyc","withdrawals","recent","quick","repayments"
        ],
        KYC_OFFICER: [
          "kyc","recent"
        ],
        FINANCE: [
          "withdrawals","repayments","recent"
        ],
        SUPPORT: [
          "users","recent"
        ],
        AUDITOR: [
          "recent"
        ]
      };

      return map[this.userRole]?.includes(key);
    },

    fmtMoney(n) {
      return new Intl.NumberFormat("en-IN").format(Number(n || 0));
    },
    async downloadDashboard () {
        const res = await exportDashboardReportXl({ admin_id: this.admin_id });
        if (!res) {
          this.toast.error("Export failed");
          return;
        }

        const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `dashboard_report_${Date.now()}.xls`;
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);

        this.toast.success("Report downloaded ✅");
      },

    statusMeta(status) {
      const map = {
        Pending: { badge: "warning", icon: "bx bx-time-five" },
        Processing: { badge: "warning", icon: "bx bx-loader-circle" },
        Completed: { badge: "success", icon: "bx bx-check-circle" },
        Rejected: { badge: "danger", icon: "bx bx-x-circle" },
        Overdue: { badge: "danger", icon: "bx bx-error-circle" },
        Paid: { badge: "success", icon: "bx bx-check-circle" },
      };
      return map[status] || { badge: "secondary", icon: "bx bx-receipt" };
    },

    async refresh() {
      try {
        this.loading = true;

        const st = await getDashboardStats(this.admin_id);
        if (st?.status) {
          this.statsData = {
            ...this.statsData,
            ...st,
          };
        }

        const rr = await getRecentRequests(this.admin_id);
        if (rr?.status) {
          this.requests = (rr.requests || []).map((r) => {
            const meta = this.statusMeta(r.status);
            return {
              ...r,
              statusClass: meta.badge,
              icon: meta.icon,
              amount: r.amount || "-",
            };
          });
        }

      } catch (e) {
        console.log("sdfwef",e);
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.toast = useToast();

    this.ldata = JSON.parse(sessionStorage.getItem("ldata") || "{}");
    this.admin_id = this.ldata?.email || "1";
    if((this.ldata.user_role+"").localeCompare("admin")==0){
      this.userRole = "SUPER_ADMIN";
    }
    else{
      this.userRole = this.ldata.user_role || "SUPER_ADMIN";
    }

    this.refresh();
  },
};
</script>

<style scoped>
  /* ===========================================
   ✅ DASHBOARD MOBILE PATCH (END PASTE ONLY)
   Desktop CSS unchanged
   =========================================== */

@media (max-width: 980px) {
  /* header stacking */
  .page-head {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  .page-head h1 {
    font-size: 20px !important;
  }

  .page-head p {
    font-size: 12px !important;
  }

  /* refresh button full-width for easy tap */
  .page-head .btn.primary {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* metrics grid -> 2 cols */
  .grid.cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 12px !important;
  }

  .metric {
    font-size: 22px !important;
  }

  /* table overflow fix */
  .card table.table {
    min-width: 760px !important; /* force scroll on small */
  }

  .card {
    overflow: hidden !important;
  }

  .card .table {
    width: 100% !important;
  }

  /* enable horizontal scroll */
  .card .table {
    display: block !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }

  /* quick actions grid -> single column */
  .grid[style*="grid-template-columns: 1fr 1fr"] {
    grid-template-columns: 1fr !important;
  }

  /* inside employee list tighten padding */
  .avatar {
    height: 34px !important;
    width: 34px !important;
  }

  /* buttons inside quick action wrap full */
  .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
  }
}

/* ✅ extra small phones */
@media (max-width: 520px) {
  /* metrics -> 1 col */
  .grid.cols-4 {
    grid-template-columns: 1fr !important;
  }

  /* card padding reduce */
  .card {
    padding: 14px !important;
    border-radius: 20px !important;
  }

  /* status pill compact */
  .status-pill {
    font-size: 10px !important;
    padding: 6px 10px !important;
  }
}

</style>
